"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import { useMobile } from "@/hooks/use-mobile"

interface ShaderButtonProps {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  className?: string
  isLoading?: boolean
  fullWidth?: boolean
  type?: "button" | "submit"
  mode?: "static" | "mesh"
  palette?: "reveal" | "primary"
  meshSpeed?: number
}

// Centralized color presets for button gradients/mesh
const PALETTES: Record<string, string[]> = {
  // Reveal: violet(600) -> magenta -> orange
  reveal: ["#7B39ED", "#E12AFB", "#FF00A8", "#FF6600"],
  // Primary: violet range only
  primary: ["#7B39ED", "#9333EA", "#8833FF", "#E12AFB"],
}

export function ShaderButton({
  children,
  onClick,
  disabled = false,
  className = "",
  isLoading = false,
  fullWidth = false,
  type = "button",
  mode = "static",
  palette = "reveal",
  meshSpeed = 1.9,
}: ShaderButtonProps) {
  const [isHovered, setIsHovered] = useState(false)
  const isMobile = useMobile()

  const colors = PALETTES[palette] || PALETTES.reveal

  // Canvas refs for mesh mode
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)

  // Animate lightweight mesh using 2D canvas (no external libs)
  useEffect(() => {
    if (mode !== "mesh" || (!isHovered && !isMobile) || disabled) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const setSize = () => {
      const { clientWidth, clientHeight } = canvas
      canvas.width = Math.max(1, Math.floor(clientWidth * dpr))
      canvas.height = Math.max(1, Math.floor(clientHeight * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    setSize()
    const ro = new ResizeObserver(setSize)
    ro.observe(canvas)

    const rgba = (hex: string, a: number) => {
      const m = hex.replace('#','')
      const bigint = parseInt(m.length === 3 ? m.split('').map(ch=>ch+ch).join('') : m, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return `rgba(${r}, ${g}, ${b}, ${a})`
    }

    const draw = (t: number) => {
      if (!startTimeRef.current) startTimeRef.current = t
      const time = (t - startTimeRef.current) / 1000

      const w = canvas.clientWidth
      const h = canvas.clientHeight
      ctx.clearRect(0, 0, w, h)

      ctx.globalCompositeOperation = "source-over"
      ctx.fillStyle = "rgba(0,0,0,0)"
      ctx.fillRect(0, 0, w, h)

      ctx.globalCompositeOperation = "lighter"

      const cx = [
        0.22 + 0.16 * Math.sin(time * meshSpeed * 1.3),
        0.78 + 0.14 * Math.cos(time * meshSpeed * 1.1),
        0.28 + 0.18 * Math.sin(time * meshSpeed * 0.9),
        0.72 + 0.12 * Math.cos(time * meshSpeed * 1.5),
      ]
      const cy = [
        0.34 + 0.14 * Math.cos(time * meshSpeed * 1.0),
        0.32 + 0.15 * Math.sin(time * meshSpeed * 1.3),
        0.76 + 0.12 * Math.cos(time * meshSpeed * 0.8),
        0.78 + 0.16 * Math.sin(time * meshSpeed * 0.7),
      ]

      const radius = Math.max(w, h) * 0.6
      for (let i = 0; i < 4; i++) {
        const x = cx[i] * w
        const y = cy[i] * h
        const grad = ctx.createRadialGradient(x, y, 0, x, y, radius)
        const c = colors[i % colors.length]
        grad.addColorStop(0, rgba(c, 0.55))
        grad.addColorStop(1, rgba(c, 0.0))
        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2)
        ctx.fill()
      }

      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)
    return () => {
      ro.disconnect()
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [mode, isHovered, isMobile, disabled, colors, meshSpeed])

  return (
    <button
      type={type}
      className={`relative overflow-hidden rounded-lg ${fullWidth ? "w-full" : ""} ${className} touch-manipulation`}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => !disabled && setIsHovered(false)}
      onClick={!disabled && !isLoading ? onClick : undefined}
      disabled={disabled || isLoading}
      aria-disabled={disabled}
    >
      {/* Base background - always visible */}
      <div className={`w-full h-full rounded-lg ${disabled ? "bg-primary/50" : "bg-violet-600"}`}></div>

      {/* Static gradient overlay */}
      {mode === "static" && (
        <div
          className={`absolute inset-0 w-full h-full rounded-lg bg-gradient-to-r from-[#7B39ED] via-[#E12AFB] to-[#FF6600] transition-opacity duration-300 ease-in-out ${
            (isHovered || isMobile) && !disabled ? "opacity-100" : "opacity-0"
          }`}
        ></div>
      )}

      {/* Mesh canvas overlay */}
      {mode === "mesh" && (
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 w-full h-full rounded-lg transition-opacity duration-300 ease-in-out ${
            (isHovered || isMobile) && !disabled ? "opacity-100" : "opacity-0"
          }`}
        />
      )}

      {/* Content */}
      <div
        className={`absolute inset-0 flex items-center justify-center z-10 ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        {children}
      </div>
    </button>
  )
}
